import {Box, SxProps, Theme} from '@mui/material';
import {usePersona} from 'src/permissions/hooks';
import {ReceiptLongOutlined} from '@mui/icons-material';
import {ChangeEvent, FC, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {invoicesApi} from 'src/api';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {store} from 'src/app/store';
import {InvoiceStatsStrip} from 'src/components/invoices/InvoiceStatsStrip';
import {InvoicesTable} from 'src/components/invoices/InvoicesTable';
import {InvoiceSortEnum, InvoiceStatusEnum} from 'src/enums';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {DataType, InvoiceSearchRequestData} from 'src/types';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

type InvoicesQueryTrigger = ReturnType<typeof invoicesApi.useLazyGetInvoicesQuery>[0];

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening an invoice and coming back restores the view instead of resetting it.
 *
 * `userId` is deliberately absent - it is not a filter anybody here sets but a consequence of who is
 * looking, and the server forces it for a caller who only ever sees their own invoices.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    status: '',
    sortField: InvoiceSortEnum.CREATED as string,
    sortAsc: false,
};

const handleGetInvoices =
    (
        trigger: InvoicesQueryTrigger,
        businessId?: number,
        userId?: number,
        /** Whether this caller only ever sees their own invoices. Decided by the component. */
        ownInvoicesOnly = false
    ) =>
        async (tableData: InvoiceSearchRequestData, params: { page: number; size: number }) => {
            store.dispatch(preloaderChangeStatus(true));

            const newData: DataType = {
                ...tableData,
                businessId: tableData.businessId ? tableData.businessId : businessId,
            };
            if (ownInvoicesOnly) {
                newData.userId = userId;
            }
            Object.keys(tableData).map((item: string) => {
                !newData[item] && delete newData[item];
            });

            const data: InvoiceSearchRequestData = {
                ...newData,
            };


            try {
                await trigger({data, params}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };


/**
 * The block and the list are two cards on one page. The top margin is the app bar's: the table used
 * to be first and carried its own, and now that the block is above it the clearance lives here.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

export const UserInvoices: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const persona = usePersona();

    const {authInfo} = useAppSelector((state) => state.common);
    const [trigger, response] = invoicesApi.useLazyGetInvoicesQuery();

    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // A boolean rather than `authInfo` itself: the effect below waits for the session to arrive, and
    // depending on the object would re-ask the server every time anything in it was replaced.
    const isSignedIn = Boolean(Object.keys(authInfo).length);

    // Both derived from the address bar rather than kept beside it.
    const filterParams: InvoiceSearchRequestData = useMemo(() => ({
        businessId: 0,
        status: query.status,
        keyword: query.keyword,
        sortAsc: query.sortAsc,
        sortField: query.sortField,
        userId: (persona.isCompany || persona.isEmployee) ? authInfo?.id : 0,
    }), [query, persona.isCompany, persona.isEmployee, authInfo?.id]);

    const tableParams = useMemo(
        () => ({page: query.page, size: query.size}),
        [query.page, query.size]
    );

    // The summary the AI agent's own tool already computes, asked with the list's filters minus the
    // one the tiles set: status is the breakdown, so honouring it would leave each tile showing
    // itself. The endpoint is the existing /invoices/summary — the reports page reads it too.
    const {data: invoiceStats} = invoicesApi.useGetInvoicesSummaryQuery({
        businessId: filterParams.businessId || businessId,
        userId: filterParams.userId || undefined,
        keyword: filterParams.keyword || undefined,
    });

    // Clicking the chosen tile again goes back to every invoice, as on every other list's block. Back
    // to the first page too: the row that was on page four of the old filter is not on page four of
    // the new one.
    const handleSelectStatus = (status: InvoiceStatusEnum) => {
        setQuery({...query, page: 0, status: query.status === status ? '' : status});
    };

    /** Whatever the list is now asking for, as a whole request body from the table below. */
    const handleChange = (value: InvoiceSearchRequestData) => {
        setQuery({
            ...query,
            page: 0,
            keyword: value.keyword ?? '',
            status: String(value.status ?? ''),
            sortField: value.sortField ?? InvoiceSortEnum.CREATED,
            sortAsc: value.sortAsc ?? false,
        });
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery({
        ...query,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    });

    // Memoised so the effect below can depend on it: `trigger` is stable and the business only
    // changes when a platform administrator switches tenant, which has to re-ask anyway.
    const callHandler = useMemo(
        () => handleGetInvoices(
            trigger,
            businessId,
            +authInfo.id,
            persona.isDriver || persona.isCompany || persona.isEmployee
        ),
        [trigger, businessId, authInfo.id, persona.isDriver, persona.isCompany, persona.isEmployee]
    );

    /**
     * One place asks the server, whenever the address bar says something else.
     *
     * Every handler above only writes the URL. Before this each of them called the server itself and
     * the only load that read the state was a mount effect with an empty dependency list - so
     * arriving with a filter already in the URL, which is what Back and a reload both are, fetched
     * the unfiltered list.
     */
    useEffect(() => {
        if (!isSignedIn) {
            return;
        }
        void callHandler(filterParams, tableParams);
    }, [isSignedIn, callHandler, filterParams, tableParams]);

    return (
        <Box sx={pageSx}>
            <InvoiceStatsStrip
                data={invoiceStats}
                activeStatus={filterParams.status}
                onSelectStatus={handleSelectStatus}
            />
            {response.data && (
                <InvoicesTable
                    data={response.data}
                    filters={filterParams}
                    size={tableParams.size}
                    handleUpdateTableData={handleChange}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    title={intl.formatMessage({id: 'invoices'})}
                    onlyTitle
                    icon={<ReceiptLongOutlined/>}
                />
            )}
        </Box>
    );
};
